import React from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout';
import { useBillingDocuments } from '../hooks/useBillingDocuments';
import { BillingDocumentsList } from '../components/billing/BillingDocumentsList';
import { DocumentIcon } from '../components/icons';

/**
 * Billing Documents Page
 *
 * Displays a list of all billing documents (VAT invoices and proformas)
 * for the current tenant.
 *
 * Features:
 * - Skeleton loading state
 * - Error state with retry button
 * - Empty state with helpful guidance
 * - Responsive table/card layout
 */
const BillingDocuments: React.FC = () => {
  const { data: documents, isLoading, error, refetch } = useBillingDocuments();

  return (
    <Layout>
      <div className="space-y-6">
        {/* Visually hidden live region for screen readers (WCAG 2.1 4.1.3) */}
        <div role="status" aria-live="polite" className="sr-only">
          {isLoading && 'Ładowanie dokumentów rozliczeniowych...'}
          {error && 'Błąd ładowania dokumentów. Spróbuj ponownie.'}
          {documents && `Załadowano ${documents.length} dokumentów.`}
        </div>

        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-neutral-900">
            Dokumenty rozliczeniowe
          </h1>
          <p className="text-neutral-600 mt-1">
            Historia faktur VAT i faktur proforma
          </p>
        </div>

        {/* Loading State - Skeleton */}
        {isLoading && (
          <>
            {/* Desktop Skeleton */}
            <div className="hidden md:block bg-white shadow-sm rounded-lg border border-neutral-200">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-neutral-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase">
                        Typ
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase">
                        Numer
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase">
                        Data
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase">
                        Kwota
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase">
                        Akcje
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-neutral-200">
                    {[1, 2, 3].map((i) => (
                      <tr key={i}>
                        <td className="px-6 py-4">
                          <div className="h-4 bg-neutral-200 rounded animate-pulse w-20" />
                        </td>
                        <td className="px-6 py-4">
                          <div className="h-4 bg-neutral-200 rounded animate-pulse w-32" />
                        </td>
                        <td className="px-6 py-4">
                          <div className="h-4 bg-neutral-200 rounded animate-pulse w-24" />
                        </td>
                        <td className="px-6 py-4">
                          <div className="h-4 bg-neutral-200 rounded animate-pulse w-20" />
                        </td>
                        <td className="px-6 py-4">
                          <div className="h-4 bg-neutral-200 rounded animate-pulse w-24" />
                        </td>
                        <td className="px-6 py-4">
                          <div className="h-4 bg-neutral-200 rounded animate-pulse w-28" />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Mobile Skeleton */}
            <div className="md:hidden space-y-4">
              {[1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="bg-white border border-neutral-200 rounded-lg p-4"
                >
                  <div className="h-4 bg-neutral-200 rounded animate-pulse w-20 mb-2" />
                  <div className="h-4 bg-neutral-200 rounded animate-pulse w-32 mb-1" />
                  <div className="h-4 bg-neutral-200 rounded animate-pulse w-24 mb-3" />
                  <div className="flex gap-2">
                    <div className="h-10 bg-neutral-200 rounded animate-pulse flex-1" />
                    <div className="h-10 bg-neutral-200 rounded animate-pulse flex-1" />
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Error State with Retry */}
        {error && !isLoading && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0">
                <svg
                  className="h-5 w-5 text-red-400"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="text-sm font-medium text-red-800">
                  Nie udalo sie zaladowac dokumentow
                </h3>
                <p className="text-sm text-red-700 mt-1">
                  Wystapil nieoczekiwany blad. Sprobuj ponownie za chwile.
                </p>
                <button
                  onClick={() => refetch()}
                  className="mt-3 text-sm font-medium text-red-700 hover:text-red-800 underline"
                >
                  Sprobuj ponownie
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Empty State with helpful guidance */}
        {documents && documents.length === 0 && !isLoading && !error && (
          <div className="flex flex-col items-center justify-center py-16 px-4 bg-neutral-50 border border-neutral-200 rounded-lg">
            <div
              className="w-16 h-16 mb-4 text-neutral-300"
              aria-hidden="true"
            >
              <DocumentIcon className="w-full h-full" />
            </div>
            <h3 className="text-lg font-medium text-neutral-900 mb-2">
              Nie masz jeszcze żadnych dokumentów
            </h3>
            <p className="text-sm text-neutral-600 text-center max-w-md mb-4">
              Faktury VAT i proforma pojawią się tutaj automatycznie po
              aktywacji subskrypcji lub wykonaniu pierwszej płatności.
            </p>
            <Link
              to="/tenant/subscription"
              className="text-primary-600 hover:text-primary-800 text-sm font-medium"
            >
              Zobacz status subskrypcji &rarr;
            </Link>
          </div>
        )}

        {/* Documents List */}
        {documents && documents.length > 0 && !isLoading && !error && (
          <BillingDocumentsList documents={documents} />
        )}
      </div>
    </Layout>
  );
};

export default BillingDocuments;
